import PropTypes from 'prop-types';
import { useId } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
	Box,
} from '@mui/material';

/**
 * A reusable modal dialog for displaying custom content and action buttons.
 *
 * @param {boolean} open - Modal visibility
 * @param {Function} onClose - Called when the modal is dismissed (close button, backdrop click, or Escape key).
 * @param {React.ReactNode} title - Content displayed in the modal header (typically a string).
 * @param {React.ReactNode} titleIcon - Optional icon displayed next to the title in the modal header.
 * @param {React.ReactNode} children - The main body content of the modal.
 * @param {React.ReactNode} [actions] - Optional action buttons displayed at the bottom of the modal (e.g. Save, Cancel, Got it).
 * @param {'xs'|'sm'|'md'|'lg'|'xl'|false} [maxWidth='sm'] - Controls the maximum width of the modal.
 * @param {boolean} [fullWidth=true] - When true, the modal expands to the specified `maxWidth`.
 */

const BasicModal = ({
	open,
	onClose,
	title,
	titleIcon,
	children,
	actions,
	maxWidth = 'sm',
	fullWidth = true,
}) => {
	// Generate unique title ID so each mounted modal has a unique aria-labelledby target.
	const titleId = useId();

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth={fullWidth}
			maxWidth={maxWidth}
			aria-labelledby={titleId}
			slotProps={{
				paper: {
					sx: {
						borderRadius: 2,
						maxHeight: '80vh',
					},
				},
			}}
		>
			<DialogTitle
				id={titleId}
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 2,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
					}}
				>
					{titleIcon}

					<Typography variant='h6' component='span'>
						{title}
					</Typography>
				</Box>

				<IconButton onClick={onClose} aria-label='close' size='small'>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>{children}</DialogContent>

			{actions && (
				<DialogActions sx={{ px: 3, py: 2 }}>{actions}</DialogActions>
			)}
		</Dialog>
	);
};

BasicModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	title: PropTypes.node.isRequired,
	titleIcon: PropTypes.node,
	children: PropTypes.node,
	actions: PropTypes.node,
	maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', false]),
	fullWidth: PropTypes.bool,
};

export default BasicModal;
